import React, { forwardRef, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { ErrorMessage, HelperMessage } from '@atlaskit/form';
import SmartUserPicker, {
	type OnChange,
	type Value as PickerUserValue,
	type User as UserPickerUser,
	UserType,
} from '@atlaskit/smart-user-picker';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import type { UserPickerRef } from '@atlaskit/user-picker/types';

import { type InvitedUser } from '../../types/user';
import { DEBOUNCE_MS, MAX_MEMBERS } from '../../utils/constants';
import { messages } from '../i18n';

import { type MemberPickerProps } from './types';
import { CPUS_SEARCH_QUERY_FILTER } from './utils';

const SMART_USER_PICKER_ID = 'AddMemberToTeam';
const LOGGED_IN_USER = 'Context';

export const MemberPicker = forwardRef<UserPickerRef, MemberPickerProps & WithAnalyticsEventsProps>(
	(props, ref) => {
		const {
			onChange,
			maxSelectedMembers = MAX_MEMBERS,
			cloudId,
			initialValues,
			teamId,
			filterUsers,
			extraAnalyticsAttrs,
			showHelperMessages = true,
			isInvalid,
			ariaLabelledBy,
			ariaDescribedBy,
			inputId,
		} = props;

		const [hasError, setError] = useState(false);
		const [isMaximum, setIsMaximum] = useState(false);
		const [isDisabled, setDisabled] = useState(false);
		const [isOpen, setOpen] = useState(false);
		const [query, setQuery] = useState('');
		const { formatMessage } = useIntl();
		const { fireEvent } = useAnalyticsEvents();

		const handleOnChange: OnChange = useCallback(
			(value: PickerUserValue) => {
				const selectedUsers = (value || []) as UserPickerUser[];

				setDisabled(selectedUsers.length >= maxSelectedMembers);

				setIsMaximum(selectedUsers.length === maxSelectedMembers);

				const isError = selectedUsers.length > maxSelectedMembers;
				setError(isError);
				if (isError) {
					fireEvent('track.memberPicker.error', extraAnalyticsAttrs ?? {});
				}

				if (onChange) {
					onChange(selectedUsers, { hasError: isError, isDisabled });
				}
			},
			[isDisabled, maxSelectedMembers, onChange, extraAnalyticsAttrs, fireEvent],
		);

		const handleOnInputChange = useCallback(
			(query?: string) => {
				setQuery(query || '');
			},
			[setQuery],
		);

		const handleOnBlur = useCallback(() => {
			setOpen(false);
		}, [setOpen]);

		const handleOnFocus = useCallback(() => {
			setOpen(true);
		}, [setOpen]);

		// convert InvitedUser type to (OptionalData | User) type of @atlaskit/user-picker
		const memoizedDefaultValue = useMemo(() => {
			if (!initialValues) {
				return;
			}

			return initialValues.map((user: InvitedUser) => ({
				name: user.fullName,
				publicName: user.nickname,
				type: UserType,
				...user,
			})) as UserPickerUser[];
		}, [initialValues]);

		const propsWhenHasError = hasError ? { open: false, addMoreMessage: '' } : {};

		return (
			<div>
				<SmartUserPicker
					forwardedRef={ref}
					ariaLabelledBy={ariaLabelledBy}
					aria-describedby={ariaDescribedBy}
					disableInput={isDisabled}
					onChange={handleOnChange}
					onInputChange={handleOnInputChange}
					onBlur={handleOnBlur}
					onFocus={handleOnFocus}
					open={isOpen && query.length > 0}
					includeTeams={false}
					inputId={inputId}
					fieldId={SMART_USER_PICKER_ID}
					principalId={LOGGED_IN_USER}
					isMulti
					placeholder={formatMessage(messages.userPickerPlaceholder)}
					addMoreMessage={formatMessage(messages.userPickerPlaceholder)}
					noOptionsMessage={() => formatMessage(messages.userPickerNoOption)}
					productKey={'people'}
					siteId={cloudId}
					defaultValue={memoizedDefaultValue}
					debounceTime={DEBOUNCE_MS}
					width="100%"
					containerId={teamId || ''}
					searchQueryFilter={CPUS_SEARCH_QUERY_FILTER}
					filterOptions={filterUsers}
					maxOptions={MAX_MEMBERS}
					bootstrapOptions={[]}
					isInvalid={hasError || isInvalid}
					{...propsWhenHasError}
				/>

				{showHelperMessages &&
					(hasError ? (
						<ErrorMessage>
							{formatMessage(messages.limitedUsersInvitationMessageError, {
								maxNumber: maxSelectedMembers,
							})}
						</ErrorMessage>
					) : isMaximum ? (
						<ErrorMessage>
							{formatMessage(messages.limitedUsersInvitationMessageMaximumError, {
								maxNumber: maxSelectedMembers,
							})}
						</ErrorMessage>
					) : (
						<HelperMessage>
							{formatMessage(messages.limitedUsersInvitationMessageInfo, {
								maxNumber: maxSelectedMembers,
							})}
						</HelperMessage>
					))}
			</div>
		);
	},
);

export default MemberPicker;
